/**
 * ColyseusClient — Connexion WebSocket native au serveur Colyseus 0.17.
 *
 * Remplace colyseus.js@0.16 (format de seat reservation incompatible).
 * Implémente directement le protocole binaire Colyseus 0.17 avec
 * @msgpack/msgpack pour le corps des messages.
 *
 * ── Protocole Colyseus 0.17 ────────────────────────────────────────────────
 *
 * 1. Matchmaking HTTP
 *    POST /matchmake/joinOrCreate/zone  { tenantId, zoneSlug, token, displayName }
 *    → { name, roomId, processId, sessionId }  (format plat, sans wrapper room{})
 *
 * 2. WebSocket
 *    ws://host/{processId}/{roomId}?sessionId={sessionId}
 *
 * 3. Handshake (à la connexion)
 *    Serveur → [JOIN_ROOM(10), len, reconnectionToken(utf8), len, serializerId(utf8)]
 *    Client  → [JOIN_ROOM(10)]  (acquittement)
 *    Ensuite le serveur délivre les messages en attente (room:state…)
 *
 * 4. Messages custom (ROOM_DATA)
 *    [13, fixstr(type), msgpackBody?]
 *    Le type est encodé en fixstr msgpack (0xa0|len + utf8) — même format que
 *    @colyseus/schema encode.string, compatible @msgpack/msgpack.
 *
 * 5. Ping (keepalive)
 *    [PING(18)] bidirectionnel — répondre immédiatement.
 *
 * ── Bytes de protocole (@colyseus/shared-types Protocol) ────────────────────
 *   JOIN_ROOM  = 10
 *   ERROR      = 11
 *   LEAVE_ROOM = 12
 *   ROOM_DATA  = 13
 *   PING       = 18
 *
 * ── Règles ──────────────────────────────────────────────────────────────────
 * - Ce service émet des événements EventBus mais ne lit jamais Zustand.
 * - Il appelle directement les méthodes de ZoneScene (upsertRemotePlayer,
 *   removeRemotePlayer) — les positions ne passent PAS par Zustand.
 * - sendMove est throttlé ici (~20 updates/s).
 */
import { encode, decode } from '@msgpack/msgpack';
import { EventBus, useGameStore } from '@campus/core';
import type { PlayerStateDTO, UserStatus, SessionRevokedReason, ScooterSnapshot, GameEventMap } from '@campus/core';
import type { ZoneScene } from '../scenes/ZoneScene.js';
import { useHandsStore } from '../../stores/handsStore.js';

// ── Protocole ──────────────────────────────────────────────────────────────

const Protocol = {
  JOIN_ROOM:  10,
  ERROR:      11,
  LEAVE_ROOM: 12,
  ROOM_DATA:  13,
  PING:       18,
} as const;

// ── URLs ───────────────────────────────────────────────────────────────────

function getRealtimeWsUrl(): string {
  if (import.meta.env.VITE_REALTIME_URL) {
    return import.meta.env.VITE_REALTIME_URL as string;
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/colyseus`;
}

function wsToHttp(url: string): string {
  return url.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

// ── Types internes ─────────────────────────────────────────────────────────

const SEND_INTERVAL_MS = 50; // ~20 updates/s

// Chien de garde : connexion morte sans avoir été fermée (veille de
// l'ordinateur, réseau coupé net). Les pings de ws-transport (toutes les 3 s)
// sont des trames de protocole WebSocket auxquelles le navigateur répond seul :
// la page ne les voit jamais. Le client envoie donc lui-même un PING à chaque
// tour, que la room renvoie aussitôt (Room._onMessage) — sans aucun message
// pendant SILENCE_TIMEOUT_MS, la connexion est perdue. Avant (jusqu'au
// 2026-10-15) : on attendait des PING qui n'arrivaient jamais, et une carte
// calme 20 s (personne ne bouge ni n'écrit) déclenchait une fausse reconnexion.
//
// Sensibilité : SILENCE_TIMEOUT_MS = temps de silence toléré avant de
// reconnecter. Côté serveur, ws-transport coupe aussi un client qui ne répond
// plus à ses pings de protocole après ~6–9 s (pingInterval 3 s × pingMaxRetries 2,
// apps/realtime/src/index.ts).
const SILENCE_TIMEOUT_MS = 20_000;
const WATCHDOG_INTERVAL_MS = 5_000;

// Fermetures voulues par le serveur, déjà annoncées par un message
// (moderation:kicked, auth:session-revoked, session:replaced) ou anti-abus :
// jamais de reconnexion automatique.
const NO_RECONNECT_CODES = new Set([
  4401, // session révoquée (mot de passe ou rôle changé)
  4403, // expulsion par un admin
  4409, // campus ouvert dans une autre fenêtre
  4429, // anti-spam du chat
  4410, // carte désactivée (zone:closed déjà reçu)
]);

export type JoinResult = 'joined' | 'failed' | 'superseded';

interface JoinParams {
  tenantId: string;
  slug: string;
  displayName: string;
  avatarIndex: number;
  appearanceJson: string;
}

interface PlayerSnapshot {
  sessionId:   string;
  userId:      string;
  displayName: string;
  x:           number;
  y:           number;
  direction:   string;
  animation:   string;
  bubbleId:    string;
  avatarIndex?: number;
  status?:     string;
  appearanceJson?: string;
  vehicle?:    string;
  seat?:       string;
  handRaised?: boolean;
}

// ── Client ─────────────────────────────────────────────────────────────────

export class ColyseusClient {
  private ws: WebSocket | null = null;
  private mySessionId: string | null = null;
  private scene: ZoneScene | null = null;
  private lastSendTime = 0;
  private scooterSnapshots = new Map<string, ScooterSnapshot>();
  // type → handler, peuplé dans bindHandlers() après handshake
  private readonly handlers = new Map<string, (data: unknown) => void>();
  // userId → bubbleId pour TOUS les joueurs de la room (local + distants).
  // Nécessaire pour que LiveKitService.shouldSubscribeTo() puisse filtrer par bulle.
  private readonly allPlayerBubbles = new Map<string, string | null>();
  // userId → { userId, displayName, status } — tous les joueurs de la zone pour le HUD.
  private readonly allZonePlayers = new Map<string, { userId: string; displayName: string; status: string }>();
  // sessionId → snapshot en attente, reçus avant que setScene() ne soit appelé.
  private readonly pendingRemoteSnapshots = new Map<string, PlayerSnapshot>();
  // Dernière salle rejointe — sert à la reconnexion automatique. Vidé par
  // disconnect() (départ voulu : changement de carte, déconnexion…).
  private lastJoin: JoinParams | null = null;
  // Incrémenté à chaque connect()/disconnect() : un connect() dépassé par un
  // autre ne doit rien conclure (ni succès, ni échec à rattraper).
  private joinGeneration = 0;
  private lastMessageAt = 0;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private onVisible: (() => void) | null = null;

  // ── API publique ───────────────────────────────────────────

  setScene(scene: ZoneScene): void {
    this.scene = scene;
    // Trottinettes reçues avant que la scène ne soit prête.
    scene.setScooterSnapshots([...this.scooterSnapshots.values()]);
    console.log('[ColyseusClient] setScene called — scene is now set');
    // Rejoue les snapshots distants reçus AVANT que la scène soit prête
    // (room:state/player:join arrivés trop tôt) — sinon ces joueurs restent
    // invisibles tant qu'aucun nouveau player:update ne les concerne.
    if (this.pendingRemoteSnapshots.size > 0) {
      const pending = [...this.pendingRemoteSnapshots.values()];
      this.pendingRemoteSnapshots.clear();
      for (const p of pending) this.upsertRemoteSprite(p);
    }
  }

  getScene(): ZoneScene | null {
    return this.scene;
  }

  async connect(
    tenantId: string,
    slug: string,
    jwt: string,
    displayName: string,
    avatarIndex = 0,
    appearanceJson = '',
    spawnX?: number,
    spawnY?: number,
  ): Promise<JoinResult> {
    this.disconnect();
    const generation = this.joinGeneration;
    this.lastJoin = { tenantId, slug, displayName, avatarIndex, appearanceJson };

    const wsBase   = getRealtimeWsUrl();
    const httpBase = wsToHttp(wsBase);

    try {
      // ── 1. Matchmaking HTTP ───────────────────────────────────
      console.log(`[ColyseusClient] POST ${httpBase}/matchmake/joinOrCreate/zone`);

      const res = await fetch(`${httpBase}/matchmake/joinOrCreate/zone`, {
        method:  'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        // spawnX/spawnY : position réelle calculée depuis le TMJ côté client — le serveur
        // s'en sert comme position initiale autoritaire pour ne jamais laisser le joueur
        // "coincé" à une position par défaut invisible pour la proximité/les bulles tant
        // qu'il n'a pas envoyé son premier player:moved.
        body:    JSON.stringify({ tenantId, zoneSlug: slug, token: jwt, displayName, avatarIndex, appearanceJson, spawnX, spawnY }),
      });

      if (!res.ok) {
        throw new Error(`Matchmake HTTP ${res.status}: ${await res.text()}`);
      }

      // Colyseus 0.17 : format plat (sans wrapper room:{})
      const seat = await res.json() as {
        name: string; roomId: string; processId: string; sessionId: string;
      };
      console.log('[ColyseusClient] Seat reservation:', seat);
      // Départ ou autre connexion pendant la réservation : ne pas écraser la socket courante.
      if (generation !== this.joinGeneration) return 'superseded';

      this.mySessionId = seat.sessionId;

      // ── 2. WebSocket + handshake ──────────────────────────────
      const wsUrl = `${wsBase}/${seat.processId}/${seat.roomId}?sessionId=${seat.sessionId}`;
      console.log(`[ColyseusClient] WS → ${wsUrl}`);

      await this.openWebSocket(wsUrl, tenantId, slug);
      if (generation !== this.joinGeneration) return 'superseded';
      // Nouvelle salle (changement de carte, reconnexion) : le serveur part de
      // « Disponible », on lui redonne le statut en cours.
      const status = useGameStore.getState().localStatus;
      if (status !== 'available') this.sendStatus(status);
      return 'joined';

    } catch (err) {
      if (generation !== this.joinGeneration) return 'superseded';
      console.error('[ColyseusClient] Failed to join zone:', err);
      // Connexion à moitié ouverte : la fermer, sans oublier lastJoin.
      this.dropSocket();
      // Si le serveur a refusé la connexion car l'utilisateur est kické,
      // déclencher le flux de kick côté client (→ retour LoginPage).
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('KICKED')) {
        console.log('[ColyseusClient] Détecté KICKED — émission moderation:kicked');
        EventBus.emit('moderation:kicked', {});
      }
      // Carte désactivée entre-temps : départ vers l'arrivée, pas de nouvel essai.
      if (msg.includes('ZONE_DISABLED')) {
        EventBus.emit('zone:closed', {});
        return 'superseded';
      }
      return 'failed';
    }
  }

  /**
   * Rejoint la dernière salle quittée sans le vouloir, à la position actuelle
   * de l'avatar. `superseded` si plus rien à rejoindre (départ voulu entre-temps).
   */
  async reconnect(jwt: string): Promise<JoinResult> {
    const join = this.lastJoin;
    if (!join) return 'superseded';
    const pos = this.scene?.getLocalPlayerPosition() ?? null;
    return this.connect(
      join.tenantId, join.slug, jwt, join.displayName, join.avatarIndex, join.appearanceJson,
      pos?.x, pos?.y,
    );
  }

  isConnected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN && this.handlers.size > 0;
  }

  /** Départ voulu (changement de carte, déconnexion, expulsion) : pas de reconnexion. */
  disconnect(): void {
    this.joinGeneration++;
    this.lastJoin = null;
    this.dropSocket(true);
  }

  private dropSocket(sendLeave = false): void {
    this.stopWatchdog();
    const ws = this.ws;
    this.ws = null;
    this.mySessionId = null;
    this.handlers.clear();
    this.pendingRemoteSnapshots.clear();
    this.scooterSnapshots.clear();
    if (!ws) return;
    ws.onclose = null;
    ws.onmessage = null;
    ws.onerror = null;
    try {
      if (sendLeave && ws.readyState === WebSocket.OPEN) {
        ws.send(new Uint8Array([Protocol.LEAVE_ROOM]));
      }
      ws.close(1000);
    } catch { /* already closed */ }
  }

  /**
   * Connexion perdue sans départ voulu : redémarrage de campus-realtime
   * (déploiement), réseau, veille. Les avatars distants sont retirés (ils
   * resteraient figés) et GameBridge relance la connexion.
   */
  private handleConnectionLost(ws: WebSocket, code: number): void {
    if (this.ws !== ws) return; // déjà remplacée ou fermée volontairement
    console.warn(`[ColyseusClient] connexion perdue (code=${code})`);
    this.dropSocket();
    this.scene?.removeAllRemotePlayers();
    if (NO_RECONNECT_CODES.has(code)) return;
    EventBus.emit('colyseus:connection-lost', { code });
  }

  private startWatchdog(ws: WebSocket): void {
    this.stopWatchdog();
    this.lastMessageAt = Date.now();
    const ping = () => {
      try { if (ws.readyState === WebSocket.OPEN) ws.send(new Uint8Array([Protocol.PING])); } catch { /* fermée */ }
    };
    this.watchdog = setInterval(() => {
      // Onglet en arrière-plan : Chrome y ralentit les minuteries (jusqu'à une
      // fois par minute) — le silence mesuré serait le nôtre, pas celui du
      // serveur. Une socket vraiment fermée déclenche quand même onclose.
      if (!document.hidden && Date.now() - this.lastMessageAt > SILENCE_TIMEOUT_MS) {
        this.handleConnectionLost(ws, 0);
        return;
      }
      ping();
    }, WATCHDOG_INTERVAL_MS);
    // Retour sur l'onglet : délai de grâce, et un PING tout de suite pour vérifier.
    this.onVisible = () => {
      if (document.hidden) return;
      this.lastMessageAt = Date.now();
      ping();
    };
    document.addEventListener('visibilitychange', this.onVisible);
  }

  private stopWatchdog(): void {
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = null;
    if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
    this.onVisible = null;
  }

  /** Monter sur une trottinette (le serveur vérifie qu'elle est libre et à portée). */
  sendScooterMount(req: { itemId: string; model: string; x: number; y: number; direction: string }): void {
    this.sendRoomData('scooter:mount', req);
  }

  sendScooterDismount(): void {
    this.sendRoomData('scooter:dismount', {});
  }

  sendSeatSit(req: { x: number; y: number; direction: string; lift: number }): void {
    this.sendRoomData('seat:sit', req);
  }

  sendSeatStand(): void {
    this.sendRoomData('seat:stand', {});
  }

  /** Entrer dans la cabine : le serveur répond par la place (elevator:slot) ou elevator:full. */
  sendElevatorBoard(req: { zoneId: string; direction: 'up' | 'down' }): void {
    this.sendRoomData('elevator:board', req);
  }

  sendElevatorArrive(req: { zoneId: string; slot: number; x: number; y: number }): void {
    this.sendRoomData('elevator:arrive', req);
  }

  sendMove(x: number, y: number, direction: string, animation: string): void {
    const now = Date.now();
    // L'arrêt (idle) doit toujours passer — ne pas throttler le changement walk→idle,
    // sinon les clients distants voient l'animation walk figée indéfiniment.
    if (animation !== 'idle' && now - this.lastSendTime < SEND_INTERVAL_MS) return;
    this.lastSendTime = now;
    this.sendRoomData('move', { x, y, direction, animation });
  }

  sendChat(text: string): void {
    this.sendRoomData('chat:proximity', { text });
  }

  /** Chat de réunion (admins) : épingler un message, réserver l'écriture. */
  sendMeetingChatPin(messageId: string | null): void {
    this.sendRoomData('meeting-chat:pin', { messageId });
  }

  sendMeetingChatLock(locked: boolean): void {
    this.sendRoomData('meeting-chat:lock', { locked });
  }

  sendStatus(status: string): void {
    this.sendRoomData('status:change', { status });
  }

  sendWave(targetUserId: string): void {
    console.log('[ColyseusClient] sendWave →', targetUserId);
    this.sendRoomData('wave:sent', { targetUserId });
  }

  sendReaction(emoji: string): void {
    this.sendRoomData('reaction:send', { emoji });
  }

  sendProfileUpdate(displayName: string, avatarIndex: number): void {
    this.sendRoomData('profile:update', { displayName, avatarIndex });
  }

  sendAppearanceUpdate(appearanceJson: string): void {
    this.sendRoomData('appearance:update', { appearanceJson });
  }

  sendBoardOpen(itemId: string): void { this.sendRoomData('board:open', { itemId }); }
  sendBoardClose(): void { this.sendRoomData('board:close', {}); }
  sendBoardUpdate(itemId: string, elements: unknown[]): void { this.sendRoomData('board:update', { itemId, elements }); }
  sendBoardPointer(itemId: string, x: number, y: number, button: 'down' | 'up'): void {
    this.sendRoomData('board:pointer', { itemId, x, y, button });
  }

  sendLockBubble(bubbleId: string, locked: boolean): void {
    this.sendRoomData('lock:bubble', { bubbleId, locked });
  }

  sendKnockRequest(bubbleId: string): void {
    this.sendRoomData('knock:request', { bubbleId });
  }

  sendKnockRespond(userId: string, bubbleId: string, granted: boolean): void {
    this.sendRoomData('knock:respond', { userId, bubbleId, granted });
  }

  // ── Sprint 11d : Podium ────────────────────────────────────
  sendPodiumEnter(): void {
    this.sendRoomData('podium:enter', {});
  }
  sendPodiumLeave(): void {
    this.sendRoomData('podium:leave', {});
  }
  sendHandRaise(): void {
    this.sendRoomData('hand:raised', {});
  }
  /** Lever / baisser la main dans sa bulle (salle de réunion). */
  sendBubbleHand(raised: boolean): void {
    this.sendRoomData('bubble:hand', { raised });
  }
  sendHandGrant(userId: string): void {
    this.sendRoomData('hand:granted', { userId });
  }

  // ── WebSocket ──────────────────────────────────────────────

  /**
   * Ouvre la connexion WebSocket et attend le handshake JOIN_ROOM.
   * Résout quand le handshake est acquitté et les handlers sont prêts.
   */
  private openWebSocket(wsUrl: string, tenantId: string, slug: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      // Timeout de connexion — évite d'attendre indéfiniment
      const timeoutId = setTimeout(() => {
        reject(new Error('WebSocket handshake timeout (10s)'));
        ws.close();
      }, 10_000);

      ws.onopen = () => {
        console.log('[ColyseusClient] WS open — waiting for JOIN_ROOM');
      };

      // Phase handshake — remplacé par onMessage après JOIN_ROOM
      ws.onmessage = (event: MessageEvent) => {
        const buf = new Uint8Array(event.data as ArrayBuffer);
        const code = buf[0];

        if (code === Protocol.JOIN_ROOM) {
          // Handshake : [10, len, token(utf8), len, serializerId(utf8), handshake?]
          const tokenLen = buf[1] ?? 0;
          const reconnectionToken = new TextDecoder().decode(buf.slice(2, 2 + tokenLen));
          console.log(
            `[ColyseusClient] JOIN_ROOM — session=${this.mySessionId}`,
            `reconnectionToken=${reconnectionToken}`,
            `tenantId=${tenantId} slug=${slug} scene=${!!this.scene}`,
          );

          // Acquittement JOIN_ROOM
          ws.send(new Uint8Array([Protocol.JOIN_ROOM]));

          // Handlers prêts AVANT de passer en mode normal
          // (le serveur envoie room:state immédiatement après l'ack)
          this.bindHandlers();
          ws.onmessage = (ev: MessageEvent) => {
            this.lastMessageAt = Date.now();
            this.onMessage(ev);
          };
          // Après le handshake, toute fermeture non voulue est une perte de connexion.
          ws.onclose = (ev: CloseEvent) => this.handleConnectionLost(ws, ev.code);
          this.startWatchdog(ws);

          clearTimeout(timeoutId);
          resolve();
          return;
        }

        if (code === Protocol.PING) {
          ws.send(new Uint8Array([Protocol.PING]));
          return;
        }

        if (code === Protocol.ERROR) {
          clearTimeout(timeoutId);
          reject(new Error(`Server error during join: ${this.parseError(buf)}`));
          return;
        }
      };

      ws.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error('WebSocket connection error'));
      };

      // Fermeture pendant le handshake (y compris propre : serveur en arrêt).
      ws.onclose = (e) => {
        clearTimeout(timeoutId);
        reject(new Error(`WS closed during join (code=${e.code})`));
      };
    });
  }

  /** Handler normal (post-handshake) pour tous les messages entrants. */
  private onMessage(event: MessageEvent): void {
    const buf = new Uint8Array(event.data as ArrayBuffer);
    const code = buf[0];

    switch (code) {
      case Protocol.PING:
        // Réponse de la room à notre PING (chien de garde) : ne pas la renvoyer,
        // sinon client et room se la renverraient sans fin.
        break;

      case Protocol.ROOM_DATA:
        this.dispatchRoomData(buf);
        break;

      case Protocol.LEAVE_ROOM:
        console.log('[ColyseusClient] Server-initiated leave');
        EventBus.emit('moderation:kicked', {});
        break;

      case Protocol.ERROR:
        console.error('[ColyseusClient] Room error:', this.parseError(buf));
        break;

      default:
        // ROOM_STATE (14) / ROOM_STATE_PATCH (15) — schema binaire ignoré
        // (on utilise uniquement les messages JSON via ROOM_DATA)
        break;
    }
  }

  // ── Encodage / décodage ──────────────────────────────────

  /**
   * Envoie un message ROOM_DATA au serveur.
   * Format : [13, fixstr(type), msgpackBody?]
   * encode(type) produit du fixstr msgpack, identique au format @colyseus/schema.
   */
  private sendRoomData(type: string, data?: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const typeBytes = encode(type);
    const bodyBytes = data !== undefined ? encode(data) : new Uint8Array(0);

    const buf = new Uint8Array(1 + typeBytes.length + bodyBytes.length);
    buf[0] = Protocol.ROOM_DATA;
    buf.set(typeBytes, 1);
    buf.set(bodyBytes, 1 + typeBytes.length);

    this.ws.send(buf);
  }

  /**
   * Décode un message ROOM_DATA entrant.
   *
   * Layout : [13, prefix, ...typeUtf8, ...msgpackBody]
   *   fixstr (0xa1–0xbf) : prefix = 0xa0|len, type démarre à offset 2
   *   str8  (0xd9)       : length à offset 2, type à offset 3
   *   str16 (0xda)       : length (2 bytes) à offsets 2–3, type à offset 4
   */
  private dispatchRoomData(buf: Uint8Array): void {
    const prefix = buf[1] ?? 0;
    let typeStart: number;
    let typeLen: number;

    if (prefix >= 0xa1 && prefix <= 0xbf) {
      // fixstr — couvre tous nos message types (< 32 chars)
      typeLen   = prefix & 0x1f;
      typeStart = 2;
    } else if (prefix === 0xd9) {
      // str8
      typeLen   = buf[2] ?? 0;
      typeStart = 3;
    } else if (prefix === 0xda) {
      // str16
      typeLen   = ((buf[2] ?? 0) << 8) | (buf[3] ?? 0);
      typeStart = 4;
    } else {
      console.warn('[ColyseusClient] ROOM_DATA: type prefix inattendu', prefix.toString(16));
      return;
    }

    const type       = new TextDecoder().decode(buf.slice(typeStart, typeStart + typeLen));
    const bodyOffset = typeStart + typeLen;
    const body       = bodyOffset < buf.length
      ? decode(buf.slice(bodyOffset))
      : undefined;

    console.log(`[ColyseusClient] ROOM_DATA type="${type}"`, body);

    const handler = this.handlers.get(type);
    if (handler) {
      handler(body);
    } else {
      console.warn(`[ColyseusClient] Aucun handler pour le type "${type}"`);
    }
  }

  /**
   * Décode le message d'erreur Colyseus.
   *
   * Format wire : [11(ERROR), codeBytes(LE uint16), msgpackString(message)]
   *   - Code : 0xCD + 2 bytes little-endian (custom @colyseus/schema encode.number)
   *             ex. 526 → [0xCD, 0x0E, 0x02]
   *   - Message : msgpack str (fixstr / str8 / str16)
   */
  private parseError(buf: Uint8Array): string {
    try {
      // Décoder le code (LE uint16 custom Colyseus)
      let code: number;
      let msgOffset: number;
      if (buf[1] === 0xCD) {
        code = ((buf[3] ?? 0) << 8) | (buf[2] ?? 0); // little-endian
        msgOffset = 4;
      } else if (buf[1] === 0xCC) {
        code = buf[2] ?? 0;
        msgOffset = 3;
      } else {
        code = buf[1] ?? 0;
        msgOffset = 2;
      }

      // Décoder la chaîne de message (msgpack standard)
      let message = '';
      if (msgOffset < buf.length) {
        message = decode(buf.slice(msgOffset)) as string;
      }

      return `code=${code} "${message}"`;
    } catch {
      return `raw=[${buf.slice(1, 10).join(',')}]`;
    }
  }

  // ── Handlers messages ────────────────────────────────────

  /**
   * Peuple this.handlers avec les callbacks pour chaque type de message.
   * Appelé AVANT de passer ws.onmessage en mode normal pour éviter
   * de rater room:state qui arrive immédiatement après l'ack JOIN_ROOM.
   *
   * ── Boucle de synchronisation ────────────────────────────────────────────
   *
   *  [Input local]
   *      ↓  handleInput() → EventBus 'player:moved'
   *  [GameBridge]
   *      ↓  sendMove(x, y, direction, animation)
   *  [Serveur ZoneRoom.handleMove]
   *      ↓  state.players.get(sessionId).x = x  (autorité serveur)
   *      ↓  broadcast('player:update', snap)     (tous les clients)
   *  [ColyseusClient handler 'player:update']
   *      ↓  si REMOTE → upsertRemoteSprite()     (met à jour le sprite)
   *      ↓  si LOCAL  → emitBubbleUpdate()       (client-side prediction,
   *                                               pas de correction de pos.)
   */
  private bindHandlers(): void {
    const mySessionId = this.mySessionId!;

    // ── room:state — snapshot INITIAL de tous les joueurs présents ──────────
    // Reçu UNE SEULE FOIS, immédiatement après l'ack JOIN_ROOM.
    // LOCAL  → snapper la position du sprite au spawn autorité du serveur,
    //          sinon l'anti-teleport (MAX_DELTA=200) rejette les premiers moves.
    // REMOTE → créer le sprite distant.
    this.handlers.set('room:state', (raw) => {
      const data = raw as { players: PlayerSnapshot[] };
      console.log('[ColyseusClient] room:state — players:', data.players.length, 'scene:', !!this.scene);
      // Peupler les maps complètes AVANT d'émettre (reconnexion : les
      // joueurs partis entre-temps ne doivent pas rester).
      this.allZonePlayers.clear();
      this.allPlayerBubbles.clear();
      useHandsStore.getState().reset(data.players.filter((p) => p.handRaised).map((p) => p.userId));
      for (const p of data.players) {
        this.allPlayerBubbles.set(p.userId, p.bubbleId || null);
        this.allZonePlayers.set(p.userId, { userId: p.userId, displayName: p.displayName, status: p.status ?? 'available' });
      }
      let localBubbleId: string | null = null;
      for (const p of data.players) {
        if (p.sessionId === mySessionId) {
          localBubbleId = p.bubbleId || null;
          this.scene?.setLocalVehicle(p.vehicle ?? '');
          this.scene?.setLocalSeat(p.seat ?? '');
          // Ne pas écraser la position du client si le serveur a la position par défaut (100,100).
          // Le client a déjà la bonne position depuis le spawn TMJ.
          // Le premier player:moved informera le serveur de la position réelle.
          if (p.x !== 100 || p.y !== 100) {
            this.scene?.syncLocalPlayerSpawn(p.x, p.y);
          }
        } else {
          this.upsertRemoteSprite(p);
        }
      }
      EventBus.emit('colyseus:local-bubble-changed', { bubbleId: localBubbleId });
      EventBus.emit('colyseus:bubble-ids-updated', new Map(this.allPlayerBubbles));
      EventBus.emit('colyseus:zone-players-updated', new Map(this.allZonePlayers));
      // Avatars des autres connus : l'avatar qui arrive par l'ascenseur peut choisir sa place.
      this.scene?.onRoomStateApplied();
    });

    // ── player:join — un AUTRE joueur arrive dans la room ───────────────────
    // Le serveur envoie ce message avec { except: client } → on ne reçoit
    // JAMAIS son propre join ici. La garde défensive reste pour robustesse.
    this.handlers.set('player:join', (raw) => {
      const p = raw as PlayerSnapshot;
      console.log('[ColyseusClient] player:join — sessionId:', p.sessionId, 'userId:', p.userId);
      if (p.sessionId === mySessionId) return; // garde défensive
      this.allPlayerBubbles.set(p.userId, p.bubbleId || null);
      this.allZonePlayers.set(p.userId, { userId: p.userId, displayName: p.displayName, status: p.status ?? 'available' });
      this.upsertRemoteSprite(p);
      EventBus.emit('colyseus:bubble-ids-updated', new Map(this.allPlayerBubbles));
      EventBus.emit('colyseus:zone-players-updated', new Map(this.allZonePlayers));
    });

    // ── player:update — position confirmée par le serveur ───────────────────
    // Serveur broadcast à TOUS les clients (y compris l'émetteur du move).
    // REMOTE → interpoler / déplacer le sprite distant.
    // LOCAL  → mettre à jour la bulle uniquement (client-side prediction :
    //          l'input local fait autorité sur la position, pas le serveur).
    this.handlers.set('player:update', (raw) => {
      const p = raw as PlayerSnapshot;
      // Mettre à jour la map globale pour TOUS les joueurs à chaque move.
      const bubbleId = p.bubbleId || null;
      const bubbleChanged = (this.allPlayerBubbles.get(p.userId) ?? null) !== bubbleId;
      this.allPlayerBubbles.set(p.userId, bubbleId);
      // Un autre joueur change de bulle : vignettes, souscriptions LiveKit et
      // compteur suivent tout de suite. Avant, la liste n'était republiée
      // qu'aux déplacements du joueur local : quelqu'un d'assis (immobile)
      // ne voyait pas la vignette d'une personne entrée après lui.
      if (bubbleChanged && p.sessionId !== mySessionId) {
        EventBus.emit('colyseus:bubble-ids-updated', new Map(this.allPlayerBubbles));
      }
      // Main levée par quelqu'un de ma bulle : on le dit (la vignette porte aussi une main).
      const hands = useHandsStore.getState();
      if (p.handRaised && !hands.raised.has(p.userId) && p.sessionId !== mySessionId
        && p.bubbleId && p.bubbleId === useGameStore.getState().localBubbleId) {
        EventBus.emit('ui:toast', { message: `${p.displayName} lève la main.`, tone: 'info' });
      }
      hands.set(p.userId, !!p.handRaised);
      // Mettre à jour le status dans allZonePlayers si changé
      const zp = this.allZonePlayers.get(p.userId);
      if (zp && p.status && zp.status !== p.status) {
        zp.status = p.status;
        EventBus.emit('colyseus:zone-players-updated', new Map(this.allZonePlayers));
      }
      if (p.sessionId === mySessionId) {
        this.emitBubbleUpdate(p);
        this.scene?.setLocalVehicle(p.vehicle ?? '');
        this.scene?.setLocalSeat(p.seat ?? '');
      } else {
        this.upsertRemoteSprite(p);
      }
    });

    // ── Trottinettes : état de la room puis changements (game/systems/Scooters.ts)
    this.handlers.set('scooters:state', (raw) => {
      const data = raw as { scooters: ScooterSnapshot[] };
      this.scooterSnapshots = new Map(data.scooters.map((s) => [s.itemId, s]));
      this.scene?.setScooterSnapshots(data.scooters);
    });
    this.handlers.set('scooter:changed', (raw) => {
      const s = raw as ScooterSnapshot;
      this.scooterSnapshots.set(s.itemId, s);
      this.scene?.updateScooter(s);
    });
    this.handlers.set('scooter:refused', (raw) => {
      const { reason } = raw as { reason: string };
      if (reason === 'TAKEN') EventBus.emit('ui:toast', { message: "Quelqu'un vient de partir avec cette trottinette.", tone: 'info' });
    });
    // ── Ascenseurs : place dans la cabine, et ce que font les autres (game/systems/Elevators.ts)
    this.handlers.set('elevator:slot', (raw) => {
      const { zoneId, slot } = raw as { zoneId: string; slot: number };
      this.scene?.onElevatorSlot(zoneId, slot);
    });
    this.handlers.set('elevator:full', (raw) => {
      const { zoneId } = raw as { zoneId: string };
      this.scene?.onElevatorFull(zoneId);
    });
    this.handlers.set('elevator:boarded', (raw) => {
      const d = raw as { sessionId: string; zoneId: string; slot: number; direction: 'up' | 'down' };
      this.scene?.remoteElevatorBoarded(d.sessionId, d.zoneId, d.slot, d.direction);
    });
    this.handlers.set('elevator:arriving', (raw) => {
      const d = raw as { sessionId: string; zoneId: string; slot: number; x: number; y: number };
      this.scene?.remoteElevatorArriving(d.sessionId, d.zoneId, d.slot, d.x, d.y);
    });
    this.handlers.set('seat:refused', (raw) => {
      const { reason } = raw as { reason: string };
      if (reason === 'TAKEN') EventBus.emit('ui:toast', { message: "Quelqu'un vient de s'asseoir ici.", tone: 'info' });
      if (reason === 'NOT_YOUR_DESK') EventBus.emit('ui:toast', { message: 'Ce siège est dans le bureau de quelqu\u2019un d\u2019autre.', tone: 'info' });
      this.scene?.setLocalSeat('');
    });

    // ── player:leave — un joueur quitte la room ─────────────────────────────
    this.handlers.set('player:leave', (raw) => {
      const data = raw as { sessionId: string; userId: string };
      console.log('[ColyseusClient] player:leave — sessionId:', data.sessionId, 'userId:', data.userId);
      this.allPlayerBubbles.delete(data.userId);
      this.allZonePlayers.delete(data.userId);
      useHandsStore.getState().set(data.userId, false);
      this.pendingRemoteSnapshots.delete(data.sessionId);
      this.scene?.removeRemotePlayer(data.sessionId);
      EventBus.emit('colyseus:bubble-ids-updated', new Map(this.allPlayerBubbles));
      EventBus.emit('colyseus:zone-players-updated', new Map(this.allZonePlayers));
    });

    this.handlers.set('zone:transition-ready', (raw) => {
      const data = raw as { targetZoneId: string };
      EventBus.emit('transition:complete', { zoneId: data.targetZoneId });
    });

    this.handlers.set('moderation:kicked', () => {
      EventBus.emit('moderation:kicked', {});
    });

    this.handlers.set('auth:session-revoked', (raw) => {
      const reason = (raw as { reason?: SessionRevokedReason } | undefined)?.reason;
      EventBus.emit('auth:session-revoked', reason ? { reason } : {});
    });

    this.handlers.set('session:replaced', () => {
      EventBus.emit('auth:session-replaced', {});
    });

    this.handlers.set('zone:closed', () => {
      EventBus.emit('zone:closed', {});
    });

    this.handlers.set('moderation:muted', (raw) => {
      const data = raw as { durationMs: number };
      EventBus.emit('moderation:muted', { durationMs: data.durationMs });
    });

    this.handlers.set('broadcast:started', (raw) => {
      const data = raw as { adminUserId?: string };
      // exactOptionalPropertyTypes : on omet la clé si undefined plutôt que de passer undefined
      const payload = data.adminUserId !== undefined
        ? { adminUserId: data.adminUserId }
        : {};
      EventBus.emit('broadcast:started', payload);
    });

    this.handlers.set('broadcast:ended', () => {
      EventBus.emit('broadcast:ended', {});
    });

    // ── bubble:changed — changement de bulle du joueur LOCAL (avec hystérésis serveur)
    // Émis par le serveur uniquement quand le bubbleId change réellement.
    // Distinct de player:update qui est émis à chaque move.
    this.handlers.set('bubble:changed', (raw) => {
      const data = raw as { previousBubbleId: string; previousType: string; newBubbleId: string; newType: string };
      EventBus.emit('bubble:changed', {
        previousBubbleId: data.previousBubbleId,
        previousType: data.previousType,
        newBubbleId: data.newBubbleId,
        newType: data.newType,
      });
    });

    this.handlers.set('proximity:changed', (raw) => {
      const data = raw as { nearbyUserIds: string[] };
      EventBus.emit('proximity:changed', { nearbyUserIds: data.nearbyUserIds });
    });

    // ── zones:changed — portes/bureaux/portails édités via l'éditeur (map_zones).
    // Relais du canal Redis zones:{tenantId}:{mapId} (voir ZoneRoom) — les bulles
    // se rechargent déjà côté serveur, ceci prévient ZoneScene pour doorZones/
    // deskZones/portals (uniquement connus côté client, jamais côté Colyseus).
    this.handlers.set('zones:changed', () => {
      EventBus.emit('colyseus:zones-changed', {});
    });

    // ── walls:changed — murs édités via l'éditeur (map_walls).
    // Relais du canal Redis walls:{tenantId}:{mapId} (voir ZoneRoom) — purement
    // client-side (staticImage synthétique), rien à recharger côté serveur.
    this.handlers.set('walls:changed', () => {
      EventBus.emit('colyseus:walls-changed', {});
    });

    // ── floors:changed — sols peints via l'éditeur (map_floor_layers).
    this.handlers.set('floors:changed', () => {
      EventBus.emit('colyseus:floors-changed', {});
    });

    // ── furniture:changed — mobilier posé ou retiré via l'éditeur (map_furniture).
    this.handlers.set('furniture:changed', () => {
      EventBus.emit('colyseus:furniture-changed', {});
    });

    // ── desks:changed — bureaux personnels posés, aménagés ou libérés.
    this.handlers.set('desks:changed', () => {
      EventBus.emit('colyseus:desks-changed', {});
    });

    // ── labels:changed — étiquettes de texte posées via l'éditeur (map_labels).
    this.handlers.set('labels:changed', () => {
      EventBus.emit('colyseus:labels-changed', {});
    });

    this.handlers.set('chat:proximity', (raw) => {
      const data = raw as {
        id?: string; senderId: string; displayName: string; text: string;
        timestamp: number; isSelf?: boolean;
      };
      EventBus.emit('chat:proximity-received', data);
    });
    this.handlers.set('meeting-chat:state', (raw) => {
      EventBus.emit('meeting-chat:state', raw as GameEventMap['meeting-chat:state']);
    });
    this.handlers.set('meeting-chat:settings', (raw) => {
      EventBus.emit('meeting-chat:settings', raw as GameEventMap['meeting-chat:settings']);
    });
    this.handlers.set('chat:error', (raw) => {
      const { code } = (raw ?? {}) as { code?: string };
      EventBus.emit('ui:toast', {
        message: code === 'CHAT_LOCKED' ? 'Seuls les admins peuvent écrire dans le chat de cette réunion.' : 'Tu écris trop vite : attends quelques secondes.',
        tone: 'warning',
      });
    });

    this.handlers.set('moderation:player-muted', (raw) => {
      const data = raw as { userId: string; durationMs?: number };
      console.log('[ColyseusClient:mod] moderation:player-muted reçu', data.userId);
      EventBus.emit('moderation:player-muted', { userId: data.userId, ...(data.durationMs !== undefined && { durationMs: data.durationMs }) });
    });

    this.handlers.set('moderation:media-off', (raw) => {
      const { kind } = raw as { kind?: string };
      if (kind === 'mic' || kind === 'camera') EventBus.emit('moderation:media-off', { kind });
    });

    this.handlers.set('moderation:unmuted', () => {
      EventBus.emit('moderation:unmuted', {});
    });

    this.handlers.set('moderation:player-unmuted', (raw) => {
      const data = raw as { userId: string };
      console.log('[ColyseusClient:mod] moderation:player-unmuted reçu', data.userId);
      EventBus.emit('moderation:player-unmuted', { userId: data.userId });
    });

    this.handlers.set('whiz:received', (raw) => {
      const data = raw as { fromUserId: string };
      if (typeof data?.fromUserId === 'string') EventBus.emit('whiz:received', { fromUserId: data.fromUserId });
    });

    this.handlers.set('wave:received', (raw) => {
      const data = raw as { fromUserId: string; fromDisplayName: string };
      EventBus.emit('wave:received', { fromUserId: data.fromUserId, fromDisplayName: data.fromDisplayName });
    });

    this.handlers.set('focus_blocked', (raw) => {
      const data = raw as { targetId: string; targetName?: string };
      EventBus.emit('ui:focus-blocked', { targetId: data.targetId, targetName: data.targetName ?? '' });
    });

    this.handlers.set('reaction:received', (raw) => {
      const data = raw as { userId: string; emoji: string; sessionId: string };
      EventBus.emit('reaction:received', data);
    });

    this.handlers.set('player:profile-updated', (raw) => {
      const data = raw as { sessionId: string; userId: string; displayName: string; avatarIndex: number };
      EventBus.emit('player:profile-updated', data);
    });

    this.handlers.set('player:appearance-updated', (raw) => {
      const data = raw as { sessionId: string; userId: string; appearanceJson: string };
      EventBus.emit('player:appearance-updated', data);
    });

    // ── Tableaux blancs : traits et curseurs des autres (relais ZoneRoom) ──
    this.handlers.set('board:updated', (raw) => { EventBus.emit('board:updated', raw as GameEventMap['board:updated']); });
    this.handlers.set('board:pointer', (raw) => { EventBus.emit('board:pointer', raw as GameEventMap['board:pointer']); });
    this.handlers.set('board:pointer-left', (raw) => { EventBus.emit('board:pointer-left', raw as GameEventMap['board:pointer-left']); });
    this.handlers.set('board:closed', (raw) => { EventBus.emit('board:closed', raw as GameEventMap['board:closed']); });
    this.handlers.set('board:shared', (raw) => { EventBus.emit('board:shared', raw as GameEventMap['board:shared']); });
    this.handlers.set('board:unshared', (raw) => { EventBus.emit('board:unshared', raw as GameEventMap['board:unshared']); });

    this.handlers.set('bubble:lock-changed', (raw) => {
      const data = raw as { bubbleId: string; locked: boolean; ownerUserId?: string; ownerUserIds?: string[] };
      console.log('[ColyseusClient] bubble:lock-changed reçu:', data);
      EventBus.emit('bubble:lock-changed', data);
    });

    this.handlers.set('bubble:locks-state', (raw) => {
      const data = raw as { locks: Array<{ bubbleId: string; locked: boolean; ownerUserIds?: string[] }> };
      console.log('[ColyseusClient] bubble:locks-state reçu:', data);
      for (const lock of data.locks) {
        // Émettre un événement par owner pour peupler lockedBubbleOwners
        if (lock.ownerUserIds) {
          for (const uid of lock.ownerUserIds) {
            EventBus.emit('bubble:lock-changed', { bubbleId: lock.bubbleId, locked: true, ownerUserId: uid });
          }
        } else {
          EventBus.emit('bubble:lock-changed', lock);
        }
      }
    });

    // ── recording:changed / recording:state — bandeau RGPD "Enregistrement
    // en cours" (spec design 2026-08-20). Même pattern que bubble:lock-changed/
    // bubble:locks-state ci-dessus : recording:state (snapshot à l'arrivée)
    // ré-émet recording:changed par entrée, un seul type d'event côté store.
    this.handlers.set('recording:changed', (raw) => {
      const data = raw as { mapZoneId: string; recording: boolean; startedAt?: string };
      console.log('[ColyseusClient] recording:changed reçu:', data);
      EventBus.emit('recording:changed', data);
    });

    this.handlers.set('recording:state', (raw) => {
      const data = raw as { mapZoneIds: string[]; startedAt?: Record<string, string> };
      console.log('[ColyseusClient] recording:state reçu:', data);
      for (const mapZoneId of data.mapZoneIds) {
        const startedAt = data.startedAt?.[mapZoneId];
        EventBus.emit('recording:changed', { mapZoneId, recording: true, ...(startedAt ? { startedAt } : {}) });
      }
    });

    // Salles revendiquées par un duo : état complet à l'arrivée et à chaque changement.
    this.handlers.set('claims:state', (raw) => {
      const data = raw as { claims?: Array<{ id: string; zoneId: string; userIds: string[]; expiresAt: string }> };
      EventBus.emit('claims:changed', { claims: Array.isArray(data?.claims) ? data.claims : [] });
    });

    this.handlers.set('knock:request', (raw) => {
      const data = raw as { bubbleId: string; userId: string; displayName: string };
      EventBus.emit('knock:request', data);
    });

    this.handlers.set('knock:granted', (raw) => {
      const data = raw as { bubbleId: string };
      EventBus.emit('knock:granted', data);
    });

    this.handlers.set('knock:denied', (raw) => {
      const data = raw as { bubbleId: string };
      EventBus.emit('knock:denied', data);
    });

    // Demande de connexion (personne concernée seulement), relayée depuis Redis
    this.handlers.set('connection:changed', (raw) => {
      EventBus.emit('connection:changed', raw as GameEventMap['connection:changed']);
    });

    // ── Sprint 11d : Podium ────────────────────────────────────
    this.handlers.set('podium:state', (raw) => {
      const data = raw as { speakerId: string | null; speakerName: string | null };
      EventBus.emit('podium:state', data);
    });
    this.handlers.set('podium:mute-audience', (raw) => {
      const data = raw as { speakerId: string };
      EventBus.emit('podium:mute-audience', data);
    });
    this.handlers.set('podium:hand-raised', (raw) => {
      const data = raw as { userId: string; displayName: string };
      EventBus.emit('podium:hand-raised', data);
    });
  }

  // ── Helpers ─────────────────────────────────────────────────

  /**
   * Crée ou met à jour le sprite d'un joueur DISTANT dans la scène Phaser.
   * NE JAMAIS appeler pour le joueur local.
   */
  private upsertRemoteSprite(p: PlayerSnapshot): void {
    if (!this.scene) {
      console.warn('[ColyseusClient] upsertRemoteSprite — scene pas encore prete, snapshot mis en attente:', p.userId);
      this.pendingRemoteSnapshots.set(p.sessionId, p);
      return;
    }
    this.scene.upsertRemotePlayer({
      sessionId:   p.sessionId,
      userId:      p.userId,
      displayName: p.displayName,
      x:           p.x,
      y:           p.y,
      direction:   p.direction as PlayerStateDTO['direction'],
      animation:   p.animation as PlayerStateDTO['animation'],
      isInBubble:  !!p.bubbleId,
      status:      (p.status ?? 'available') as UserStatus,
      ...(p.avatarIndex !== undefined && { avatarIndex: p.avatarIndex }),
      ...(p.appearanceJson !== undefined && { appearanceJson: p.appearanceJson }),
      ...(p.vehicle !== undefined && { vehicle: p.vehicle }),
      ...(p.seat !== undefined && { seat: p.seat }),
    });
  }

  private emitBubbleUpdate(localSnapshot: PlayerSnapshot): void {
    if (!this.ws) return;
    const bubbleId = localSnapshot.bubbleId || null;
    this.allPlayerBubbles.set(localSnapshot.userId, bubbleId);
    EventBus.emit('colyseus:local-bubble-changed', { bubbleId });
    EventBus.emit('colyseus:bubble-ids-updated', new Map(this.allPlayerBubbles));
  }
}
